import { Box, Button, Container, Group, Stack, Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PriceList } from '@/api/generated/models';
import { useListItems, useSearchPriceLists } from '@/api/generated/price-lists/price-lists';
import {
  CardSection,
  fitColumnWidth,
  ListPagination,
  MobileFab,
  MobileListFilters,
  PageHeading,
  RowActionsMenu,
  SearchField,
  SelectField,
  StatusBanner,
  StatusPill,
  TABLE_ACTION_COL_WIDTH,
  TABLE_STATUS_COL_WIDTH,
  tableCenteredCellStyle,
  tableRowStyle
} from '@/components/common';
import { PriceListFormModal } from '@/features/price-lists/components/PriceListFormModal';
import { formatDate } from '@/utils/format';
import { openBinaryInNewTab } from '@/utils/openBinaryInNewTab';
import { usePageSize } from '@/utils/usePageSize';
import { useUrlPageParam, useUrlTextParam } from '@/utils/useUrlFilters';

const handlePreviewPriceList = (priceListId?: string) => {
  if (!priceListId) return;
  void openBinaryInNewTab(`/api/v1/price-lists/${priceListId}/pdf`);
};

const typeLabelKey = (plType?: PriceList['plType']) => {
  switch (plType) {
    case 'CONTRACT':
      return 'priceLists.typeContract';
    case 'SPECIAL':
      return 'priceLists.typeSpecial';
    default:
      return 'priceLists.typeBase';
  }
};

const validityLabel = (t: (key: string) => string, priceList: PriceList) =>
  `${formatDate(priceList.validFrom)} – ${priceList.validTo ? formatDate(priceList.validTo) : t('priceLists.noEndDate')}`;

/** Aktivnih stavki se ne vraća uz listu cjenika (samo uz pojedinačni cjenik preko
 * /items) — zato svaki red sam dohvaća svoje stavke da izračuna broj. */
const useActiveItemCount = (priceListId: string) => {
  const { data } = useListItems(priceListId);
  return data?.filter((item) => item.isActive).length ?? null;
};

const PriceListValidityCell = ({ priceList }: { priceList: PriceList }) => {
  const { t } = useTranslation();
  const activeCount = useActiveItemCount(priceList.id ?? '');
  return (
    <Text fz={13} style={{ color: 'var(--color-text-secondary)' }}>
      {validityLabel(t, priceList)}
      {activeCount !== null && ` · ${t('priceLists.activeItemsCount', { count: activeCount })}`}
    </Text>
  );
};

export const PriceListsPage = () => {
  const { t } = useTranslation();
  const [page, setPage] = useUrlPageParam();
  const [pageSize, setPageSize] = usePageSize();
  const [search, setSearch] = useUrlTextParam('search');
  const [statusFilter, setStatusFilter] = useUrlTextParam('status', 'ALL');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [createModalOpened, setCreateModalOpened] = useState(false);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  // page dolazi iz URL-a (useUrlPageParam) — na inicijalnom mountu (npr. povratak
  // s detalja preko BackButton-a) NE smije se resetirati na 1, samo kad korisnik
  // stvarno promijeni neki filter nakon toga.
  const isFirstFilterEffect = useRef(true);
  useEffect(() => {
    if (isFirstFilterEffect.current) {
      isFirstFilterEffect.current = false;
      return;
    }
    setPage(1);
  }, [debouncedSearch, statusFilter, pageSize]);

  // refetchOnMount: 'always' — lista mora biti svježa svaki put kad se uđe na
  // ekran, ne samo kad je staleTime istekao.
  const { data } = useSearchPriceLists(
    {
      page,
      pageSize,
      search: debouncedSearch.trim() || undefined,
      status: statusFilter === 'ALL' ? undefined : statusFilter
    },
    { query: { refetchOnMount: 'always' } }
  );
  const pageRows = data?.content ?? [];
  const nameColWidth = useMemo(
    () =>
      fitColumnWidth(
        pageRows.map((p) => p.name ?? ''),
        '600 13.5px',
        160,
        360
      ),
    [pageRows]
  );
  const totalElements = data?.totalElements ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const paginationLabel = t('priceLists.showing', {
    from: totalElements === 0 ? 0 : (page - 1) * pageSize + 1,
    to: Math.min(page * pageSize, totalElements),
    total: totalElements
  });

  const statusOptions = [
    { value: 'ALL', label: t('common.filters.allStatuses') },
    { value: 'ACTIVE', label: t('priceLists.statusActive') },
    { value: 'INACTIVE', label: t('priceLists.statusInactive') }
  ];

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  return (
    <Container size="lg" px={0}>
      <Stack gap={18}>
        {savedMessage && (
          <StatusBanner
            tone="success"
            message={savedMessage}
            onDismiss={() => setSavedMessage(null)}
          />
        )}

        <Group justify="space-between" wrap="nowrap" visibleFrom="sm">
          <PageHeading title={t('priceLists.title')} subtitle={t('priceLists.subtitle')} />
          <Button onClick={() => setCreateModalOpened(true)}>
            + {t('priceLists.newPriceList')}
          </Button>
        </Group>

        <Stack gap={12} hiddenFrom="sm">
          <PageHeading title={t('priceLists.title')} subtitle={t('priceLists.subtitle')} />
          <MobileFab onClick={() => setCreateModalOpened(true)}>
            + {t('priceLists.newPriceList')}
          </MobileFab>
        </Stack>

        <Box hiddenFrom="sm" w="100%">
          <MobileListFilters
            search={
              <SearchField
                hideLabel
                placeholder={t('priceLists.searchPlaceholder')}
                value={search}
                onChange={(event) => handleSearchChange(event.currentTarget.value)}
              />
            }
            filters={[
              {
                key: 'status',
                label: t('common.filters.status'),
                options: statusOptions,
                value: statusFilter,
                onChange: (value) => setStatusFilter(value ?? 'ALL'),
                emptyValue: 'ALL',
                variant: 'buttons'
              }
            ]}
          />
        </Box>

        <Group gap={16} align="flex-end" wrap="wrap" visibleFrom="sm">
          <Box w={{ base: '100%', sm: 280 }}>
            <SearchField
              hideLabel
              placeholder={t('priceLists.searchPlaceholder')}
              value={search}
              onChange={(event) => handleSearchChange(event.currentTarget.value)}
            />
          </Box>
          <Box w={220}>
            <SelectField
              hideLabel
              data={statusOptions}
              value={statusFilter}
              onChange={(value) => setStatusFilter(value ?? 'ALL')}
              allowDeselect={false}
            />
          </Box>
        </Group>

        <Box visibleFrom="sm">
          <CardSection>
            <div
              className="table-header-row"
              style={{
                ...tableRowStyle,
                padding: '12px 20px',
                backgroundColor: 'var(--color-bg-canvas)',
                borderBottom: '1px solid var(--color-border-soft)'
              }}
            >
              <Text
                fz={10}
                fw={600}
                style={{
                  width: nameColWidth,
                  color: 'var(--color-text-muted)',
                  letterSpacing: 0.4
                }}
              >
                {t('priceLists.colPriceList')}
              </Text>
              <Text
                fz={10}
                fw={600}
                w={140}
                style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
              >
                {t('priceLists.colType')}
              </Text>
              <Text
                fz={10}
                fw={600}
                style={{
                  flex: 1,
                  minWidth: 220,
                  color: 'var(--color-text-muted)',
                  letterSpacing: 0.4
                }}
              >
                {t('priceLists.colValidityAndItems')}
              </Text>
              <Text
                fz={10}
                fw={600}
                w={TABLE_STATUS_COL_WIDTH}
                ta="center"
                style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
              >
                {t('priceLists.colStatus')}
              </Text>
              <Text
                fz={10}
                fw={600}
                w={TABLE_ACTION_COL_WIDTH}
                style={{
                  ...tableCenteredCellStyle,
                  color: 'var(--color-text-muted)',
                  letterSpacing: 0.4
                }}
              >
                {t('priceLists.colAction')}
              </Text>
            </div>

            {pageRows.length === 0 ? (
              <Text fz={13} p={20} style={{ color: 'var(--color-text-muted)' }}>
                {t('common.noData')}
              </Text>
            ) : (
              pageRows.map((priceList, index) => (
                <div
                  key={priceList.id}
                  style={{
                    ...tableRowStyle,
                    padding: '14px 20px',
                    borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
                  }}
                >
                  <Stack gap={4} w={nameColWidth}>
                    <Text fz={13.5} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                      {priceList.name}
                    </Text>
                  </Stack>
                  <div style={{ width: 140 }}>
                    <StatusPill
                      label={t(typeLabelKey(priceList.plType))}
                      bg="var(--color-role-user-soft)"
                      fg="var(--color-role-user)"
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <PriceListValidityCell priceList={priceList} />
                  </div>
                  <div style={{ ...tableCenteredCellStyle, width: TABLE_STATUS_COL_WIDTH }}>
                    {priceList.isActive ? (
                      <StatusPill
                        label={t('priceLists.statusActive')}
                        bg="var(--color-status-success-soft)"
                        fg="var(--color-status-success)"
                      />
                    ) : (
                      <StatusPill
                        label={t('priceLists.statusInactive')}
                        bg="var(--color-status-draft-soft)"
                        fg="var(--color-status-draft)"
                      />
                    )}
                  </div>
                  <Group w={TABLE_ACTION_COL_WIDTH} justify="center">
                    <RowActionsMenu
                      detailsHref={`/templates/price-lists/${priceList.id}`}
                      onPreview={() => handlePreviewPriceList(priceList.id)}
                    />
                  </Group>
                </div>
              ))
            )}

            <ListPagination
              label={paginationLabel}
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
            />
          </CardSection>
        </Box>

        <Stack hiddenFrom="sm" gap={12}>
          {pageRows.length === 0 ? (
            <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
              {t('common.noData')}
            </Text>
          ) : (
            pageRows.map((priceList) => (
              <Stack
                key={priceList.id}
                gap={10}
                p={16}
                style={{
                  borderRadius: 12,
                  border: '1px solid var(--color-border-default)',
                  backgroundColor: 'var(--color-bg-surface)'
                }}
              >
                <Group justify="space-between" wrap="nowrap" align="flex-start">
                  <Text fz={14} fw={600} style={{ color: 'var(--color-text-primary)', flex: 1 }}>
                    {priceList.name}
                  </Text>
                  <RowActionsMenu
                    detailsHref={`/templates/price-lists/${priceList.id}`}
                    onPreview={() => handlePreviewPriceList(priceList.id)}
                  />
                </Group>
                <PriceListValidityCell priceList={priceList} />
                <Group gap={8} wrap="wrap">
                  <StatusPill
                    label={t(typeLabelKey(priceList.plType))}
                    bg="var(--color-role-user-soft)"
                    fg="var(--color-role-user)"
                  />
                  {priceList.isActive ? (
                    <StatusPill
                      label={t('priceLists.statusActive')}
                      bg="var(--color-status-success-soft)"
                      fg="var(--color-status-success)"
                    />
                  ) : (
                    <StatusPill
                      label={t('priceLists.statusInactive')}
                      bg="var(--color-status-draft-soft)"
                      fg="var(--color-status-draft)"
                    />
                  )}
                </Group>
              </Stack>
            ))
          )}

          <ListPagination
            label={paginationLabel}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            bordered={false}
          />
        </Stack>
      </Stack>

      <PriceListFormModal
        opened={createModalOpened}
        onClose={() => setCreateModalOpened(false)}
        onCreated={() => setCreateModalOpened(false)}
        onSaved={setSavedMessage}
      />
    </Container>
  );
};
